import {
  FOLDER_NAME_TAKEN_MESSAGE,
  FOLDER_SAVE_ERROR_MESSAGE,
  isFolderNameTaken,
  useCreateFolder,
  useUpdateFolder,
} from '@/lib/folders-queries';
import {
  FOLDER_DESCRIPTION_MAX,
  FOLDER_NAME_MAX,
  type MatrixFolder,
  createFolderInputSchema,
  updateFolderInputSchema,
} from '@mss_creator/shared';
import { Alert, TextArea, TextField } from '@pms-digital/design-system';
import { type FormEvent, type RefObject, useState } from 'react';

export const folderFormId = 'folder-form';

export type FolderDialogMode = { type: 'create' } | { type: 'rename'; folder: MatrixFolder };

export const FOLDER_NAME_REQUIRED_MESSAGE = 'Donnez un nom au dossier.';
export const FOLDER_DESCRIPTION_TOO_LONG_MESSAGE = `La description tient en ${FOLDER_DESCRIPTION_MAX} caractères au plus.`;

export interface FolderFormProps {
  mode: FolderDialogMode;
  companyId: string;
  /** Named under « Description » (« Visible par … »). */
  companyName: string;
  nameRef: RefObject<HTMLInputElement | null>;
  /** Nothing to save: closes the dialog. */
  onCancel: () => void;
  /** The folder was created or updated. */
  onSaved: (folder: MatrixFolder) => void;
  /** Called with true while the call runs, so the dialog cannot be dismissed meanwhile. */
  onPendingChange: (pending: boolean) => void;
}

/**
 * The form of « Nouveau dossier » and « Renommer le dossier » (planche 16):
 * a name, an optional description « Visible par <entreprise> ». Validated
 * with the shared Zod schemas on submit. A name already taken (409) is
 * reported under the field.
 */
export function FolderForm({
  mode,
  companyId,
  companyName,
  nameRef,
  onCancel,
  onSaved,
  onPendingChange,
}: FolderFormProps) {
  const create = useCreateFolder(companyId);
  const update = useUpdateFolder(companyId);
  const [nameError, setNameError] = useState<string | null>(null);
  const [descriptionError, setDescriptionError] = useState<string | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);
  const [description, setDescription] = useState(
    mode.type === 'rename' ? (mode.folder.description ?? '') : '',
  );
  const pending = create.isPending || update.isPending;
  const _renaming = mode.type === 'rename';

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setNameError(null);
    setDescriptionError(null);
    setSaveFailed(false);
    const name = nameRef.current?.value ?? '';

    const parsed = createFolderInputSchema.safeParse({ name, description });
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        if (issue.path[0] === 'description') {
          setDescriptionError(FOLDER_DESCRIPTION_TOO_LONG_MESSAGE);
        } else {
          setNameError(FOLDER_NAME_REQUIRED_MESSAGE);
        }
      }
      return;
    }

    onPendingChange(true);
    try {
      if (mode.type === 'create') {
        const folder = await create.mutateAsync({
          name: parsed.data.name,
          ...(parsed.data.description ? { description: parsed.data.description } : {}),
        });
        onSaved(folder);
        return;
      }
      const { folder } = mode;
      const patch = updateFolderInputSchema.safeParse({
        ...(parsed.data.name !== folder.name ? { name: parsed.data.name } : {}),
        ...(parsed.data.description !== (folder.description ?? '')
          ? { description: parsed.data.description ?? '' }
          : {}),
      });
      if (!patch.success) {
        // Nothing changed: nothing to send.
        onCancel();
        return;
      }
      onSaved(await update.mutateAsync({ folderId: folder.id, input: patch.data }));
    } catch (error) {
      if (isFolderNameTaken(error)) setNameError(FOLDER_NAME_TAKEN_MESSAGE);
      else setSaveFailed(true);
    } finally {
      onPendingChange(false);
    }
  }

  return (
    <form id={folderFormId} onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <TextField
        label="Nom"
        id="folder-name"
        ref={nameRef}
        name="name"
        defaultValue={mode.type === 'rename' ? mode.folder.name : ''}
        maxLength={FOLDER_NAME_MAX}
        autoComplete="off"
        status={nameError ? 'error' : 'default'}
        aria-invalid={nameError ? true : undefined}
        aria-describedby="folder-name-hint"
        disabled={pending}
      />
      {nameError ? (
        <Alert tone="danger">{nameError}</Alert>
      ) : (
        <p id="folder-name-hint" className="mt-1 text-caption text-gray-500">
          {FOLDER_NAME_MAX} caractères au plus, unique dans l'entreprise
        </p>
      )}

      <TextArea
        label={
          <>
            Description <span>(facultative)</span>
          </>
        }
        id="folder-description"
        name="description"
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        placeholder="À quoi sert ce dossier, pour qui, jusqu'à quand…"
        status={descriptionError ? 'error' : 'default'}
        aria-describedby="folder-description-hint"
        disabled={pending}
      />
      {descriptionError ? (
        <Alert tone="danger">{descriptionError}</Alert>
      ) : (
        <p id="folder-description-hint" className="mt-1 text-caption text-gray-500">
          Visible par {companyName}
        </p>
      )}

      {saveFailed && <Alert tone="danger">{FOLDER_SAVE_ERROR_MESSAGE}</Alert>}
    </form>
  );
}
